import classNames from "classnames";
import React, { forwardRef } from "react";

import { ignoreAttribute } from "@ascendnow/shared";

import { useChildrenArray } from "../../../lib/helpers/hooks/useChildrenArray";
import AscLink from "../../AscLink/AscLink";
import styles from "./AscDropdownOption.module.scss";

export interface AscDropdownOptionProps {
  children: React.ReactNode;
  /**
   * If href is present it will have precedence over onClick
   * @returns {void}
   */
  onClick?: () => void;
  href?: string;
  prefixIcon?: React.ReactNode;
  suffixIcon?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  active?: boolean;
  linkClassName?: string;
}

export function AscDropdownOption({ children }: AscDropdownOptionProps) {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children || null}</>;
}

interface AscDropdownOptionsContainerProps
  extends Omit<
    React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLDivElement>,
      HTMLDivElement
    >,
    "className" | "children"
  > {
  children?: JSX.Element[];
  header?: React.ReactNode;
  afterSelect?: () => void;
  className?: string;
  optionItemClassName?: string;
}

export const AscDropdownOptionsContainer = forwardRef<
  HTMLDivElement,
  AscDropdownOptionsContainerProps
>(
  (
    { children, header, afterSelect, className, optionItemClassName, ...rest },
    ref
  ) => {
    const options = useChildrenArray<AscDropdownOptionProps>(children || []);
    return (
      <div
        ref={ref}
        className={classNames(styles.selectOptionsContainer, className)}
        {...rest}
      >
        {header}
        <ul>
          {options.map((option, index) => {
            return (
              <li
                key={index}
                onClick={() => {
                  if (option?.disabled) {
                    return;
                  }
                  if (!option?.href) {
                    option?.onClick?.();
                  }
                  afterSelect?.();
                }}
                className={classNames(
                  styles.selectOption,
                  {
                    [styles.disabled]: option?.disabled,
                    [styles.active]: option?.active,
                  },
                  option?.className,
                  optionItemClassName
                )}
                {...ignoreAttribute.contrast}
              >
                <AscLink
                  className={classNames(styles.link, option?.linkClassName)}
                  href={option?.href || "#"}
                  disabled={!option?.href || option?.disabled}
                >
                  {option?.prefixIcon ? (
                    <span className={styles.icon}>{option?.prefixIcon}</span>
                  ) : null}
                  <div className={styles.content}>{option?.children}</div>
                  {option?.suffixIcon ? (
                    <span className={styles.icon}>{option?.suffixIcon}</span>
                  ) : null}
                </AscLink>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }
);
